import {
  AfterViewInit,
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  inject,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
  IonContent,
  IonFooter,
  IonHeader,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonProgressBar,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { refresh, returnUpBack } from 'ionicons/icons';
import { APP_SETTINGS_TOKEN, DEFAULT_APP_SETTINGS } from '../../services/app-settings.types';
import { ThemeService } from '../../themes/theme.service';
import { getTheme } from '../../themes/theme.config';
import { Observable, Subscription } from 'rxjs';
import { filter, finalize } from 'rxjs/operators';
import { PwaUpdateService } from '../../services/pwa-update.service';
import { CrowdctrlToastService } from '../../services/crowdctrl-toast.service';
import { LoadingService, LoadingPhase } from '../../services/loading.service';
import { UserProfileButtonComponent } from '../user-profile-button/user-profile-button.component';
import { CommonModule } from '@angular/common';
import { InfoPinService } from '../../services/info-pin.service';
import { CrowdctrlBackButtonComponent } from '../back-button/back-button.component';

// Register icons used in the header
addIcons({ refresh, returnUpBack });

/**
 * Reusable page component that provides consistent layout structure
 * for all pages across all apps. Uses Ionic components with content projection.
 */
@Component({
  selector: 'crowdctrl-page',
  host: { class: 'ion-page' },
  imports: [
    CommonModule,
    CrowdctrlBackButtonComponent,
    IonContent,
    IonFooter,
    IonHeader,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonProgressBar,
    IonRefresher,
    IonRefresherContent,
    UserProfileButtonComponent,
  ],
  templateUrl: './page.component.html',
  styleUrl: './page.component.css',
  standalone: true,
})
export class PageComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  /**
   * Title to display in the header toolbar
   */
  @Input() pageTitle: string = '';

  /**
   * Text to display in the footer.
   * If not provided, footer will be hidden.
   */
  @Input() footerText: string | null = null;

  /**
   * Whether to show the header. Defaults to true.
   */
  @Input() showHeader: boolean = true;

  /**
   * Whether to show the footer. Defaults to true if footerText is provided.
   */
  @Input() showFooter: boolean | null = null;

  /**
   * Additional CSS classes on `ion-content` (Ionic utilities, e.g. `ion-padding`, `ion-margin`).
   * Default is bottom-only so lists can stay edge-to-edge; pass `ion-padding` (or horizontal only)
   * when a route needs full scroll inset.
   */
  @Input() contentClass: string = 'ion-padding-bottom';

  /**
   * When true, `ion-content` does not scroll the primary slot; `.page-slot` fills the content area
   * so nested flex layouts (e.g. segment panes with inner scroll) receive a real height.
   * Pull-to-refresh on `ion-content` is disabled in this mode (toolbar refresh still works).
   */
  @Input({ transform: booleanAttribute }) contentSlotFill = false;

  /**
   * Default href for the back button.
   * If set, a back button will be displayed in the header.
   * Should be set for all pages except home and login pages.
   */
  @Input() defaultHref: string | null = null;

  /** When true, the toolbar back control is disabled (in addition to in-navigation pending state). */
  @Input({ transform: booleanAttribute }) backButtonDisabled = false;

  /**
   * When true, hides toolbar refresh, disables back (combined with {@link backButtonDisabled}),
   * blocks toolbar refresh / pull-to-refresh handlers, and suppresses the content refresher
   * while a route is committing (e.g. after save + navigate). Prefer this over repeating
   * `[showRefresh]` / `[backButtonDisabled]` pairs for the same flag.
   */
  @Input({ transform: booleanAttribute }) toolbarBusy = false;

  /**
   * Whether to show the refresh button in the header toolbar.
   * When enabled, clicking the refresh button will emit the refresh event.
   */
  @Input() showRefresh: boolean = false;

  /**
   * When true, pull-to-refresh runs a PWA / service worker update check when supported.
   * The refresher appears only when {@link PwaUpdateService#isEnabled} is true.
   * Can be combined with {@link showRefresh} to run an update check before the normal refresh load.
   */
  @Input() enablePwaPullRefresh = false;

  /**
   * Optional back button icon override.
   */
  @Input() backButtonIcon?: string;

  /**
   * Optional toolbar color override.
   * If provided, this will override the theme-based toolbar color.
   * If not provided, the toolbar color will be based on the current theme.
   */
  @Input() toolbarColor?: string;

  /**
   * Optional progress bar color override.
   * If provided, this will override the default progress bar color.
   * Defaults to 'medium' if not provided.
   * Use 'medium' (light gray) for blue toolbars to ensure visibility.
   */
  @Input() progressBarColor: string = 'medium';

  /**
   * Page-level load function.
   *
   * Contract:
   * - Return an Observable that **completes** when the load succeeds.
   * - Return an Observable that **errors** when the load fails.
   *
   * The `PageComponent` will:
   * - wait the configured stall period before subscribing (to avoid wasteful calls during rapid navigation)
   * - unsubscribe on navigation away (cancels Angular `HttpClient` requests)
   * - retry on re-entry until the first successful completion happens
   */
  @Input() loadFn?: () => Observable<unknown>;

  /**
   * Optional refresh function for manual refresh actions.
   * Defaults to `loadFn` when not provided.
   */
  @Input() refreshFn?: () => Observable<unknown>;

  /**
   * Event emitted when the refresh button is clicked.
   * Pages should bind to this event and call their data loading method.
   */
  @Output() refresh = new EventEmitter<void>();

  /**
   * Event emitted automatically when the page needs to load data.
   * This happens on initial load and when returning to a page that hasn't loaded yet.
   * Pages should bind to this event and call their data loading method.
   * Example: (load)="loadData()"
   */
  @Output() load = new EventEmitter<void>();

  /**
   * Computed toolbar color based on input or theme
   */
  computedToolbarColor: string = 'primary';

  /**
   * Loading state from global loading service
   */
  isLoading = false;
  loadingPhase: LoadingPhase = null;
  isLoadInFlight = false;
  isRefreshCoolingDown = false;

  /**
   * Tracks whether the page has completed a successful load at least once.
   * Used to stop auto-loading after the first success.
   */
  private hasLoadedSuccessfullyOnce = false;
  private activeLoadSubscription?: Subscription;
  private refreshCooldownTimeoutId?: number;

  /**
   * Auto-load state: wait a configurable stall period before subscribing to `loadFn`.
   * Cancel if navigation occurs before the stall completes or while the load is in-flight.
   */
  isAutoLoadStalling = false;
  private autoLoadStallMs = DEFAULT_APP_SETTINGS.requestStallMs;
  private autoLoadTimeoutId?: number;
  private autoLoadCheckTimeoutId?: number;
  private isViewActive = false;
  private hasTriggeredLoadWhileActive = false;

  private themeSubscription?: Subscription;
  private loadingSubscription?: Subscription;
  private loadingPhaseSubscription?: Subscription;
  private subscriptions = new Subscription();
  private pwaUpdateService = inject(PwaUpdateService);

  get showPwaPullRefresher(): boolean {
    return this.enablePwaPullRefresh && this.pwaUpdateService.isEnabled;
  }
  private toastService = inject(CrowdctrlToastService);
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private appSettings = inject(APP_SETTINGS_TOKEN);
  private infoPinService = inject(InfoPinService);
  private refreshCooldownMs = DEFAULT_APP_SETTINGS.refreshCooldownMs;

  pinnedContent: TemplateRef<unknown> | null = null;
  pinnedContentContext: Record<string, unknown> = {};

  constructor(private themeService: ThemeService) {}

  ngOnInit(): void {
    try {
      // Set initial toolbar color
      this.updateToolbarColor();

      // Subscribe to theme changes (only if toolbarColor is not explicitly set)
      try {
        this.themeSubscription = this.themeService.currentTheme$.subscribe(() => {
          try {
            if (!this.toolbarColor) {
              this.updateToolbarColor();
            }
          } catch (error) {
            console.error('[PageComponent] Error in theme subscription:', error);
          }
        });
      } catch (error) {
        console.error('[PageComponent] Error subscribing to theme:', error);
      }

      // Subscribe to loading state changes
      try {
        this.loadingSubscription = this.loadingService.isLoading$.subscribe((isLoading) => {
          try {
            this.isLoading = isLoading;
          } catch (error) {
            console.error('[PageComponent] Error in loading subscription:', error);
          }
        });
      } catch (error) {
        console.error('[PageComponent] Error subscribing to loading:', error);
      }

      // Cancel any pending auto-load on navigation start; re-check on navigation end.
      this.subscriptions.add(
        this.router.events
          .pipe(
            filter((event) => event instanceof NavigationStart || event instanceof NavigationEnd),
          )
          .subscribe((event) => {
            if (event instanceof NavigationStart) {
              this.cancelPendingAutoLoad();
            } else {
              this.scheduleAutoLoadCheck();
            }
          }),
      );

      // Subscribe to loading phase changes
      try {
        this.loadingPhaseSubscription = this.loadingService.loadingPhase$.subscribe((phase) => {
          try {
            this.loadingPhase = phase;
          } catch (error) {
            console.error('[PageComponent] Error in loading phase subscription:', error);
          }
        });
      } catch (error) {
        console.error('[PageComponent] Error subscribing to loading phase:', error);
      }

      this.subscriptions.add(
        this.appSettings.settings$.subscribe((settings) => {
          this.refreshCooldownMs =
            settings?.refreshCooldownMs ?? DEFAULT_APP_SETTINGS.refreshCooldownMs;
          this.autoLoadStallMs = settings?.requestStallMs ?? DEFAULT_APP_SETTINGS.requestStallMs;
        }),
      );

      // Subscribe to pinned content from info components
      this.subscriptions.add(
        this.infoPinService.getPinnedContent$().subscribe((content) => {
          this.pinnedContent = content?.template ?? null;
          this.pinnedContentContext = content?.context ?? {};
        }),
      );
    } catch (error) {
      console.error('[PageComponent] Critical error in ngOnInit:', error);
      // Ensure header still shows even if initialization fails
      this.showHeader = true;
    }
  }

  ngAfterViewInit(): void {
    // Initial activation check happens after view is rendered so visibility checks work.
    this.scheduleAutoLoadCheck();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If toolbarColor input changes, update the computed color
    if (changes['toolbarColor']) {
      this.updateToolbarColor();
    }
    if (changes['loadFn']) {
      // If the load function changes, treat it as "not loaded" and re-evaluate auto-load.
      this.hasLoadedSuccessfullyOnce = false;
      this.cancelPendingAutoLoad();
      this.cancelActiveLoad();
      this.scheduleAutoLoadCheck();
    }
  }

  ngOnDestroy(): void {
    if (this.themeSubscription) {
      this.themeSubscription.unsubscribe();
    }
    if (this.loadingSubscription) {
      this.loadingSubscription.unsubscribe();
    }
    if (this.loadingPhaseSubscription) {
      this.loadingPhaseSubscription.unsubscribe();
    }
    this.subscriptions.unsubscribe();
    this.cancelPendingAutoLoad();
    this.cancelActiveLoad();
    if (this.autoLoadCheckTimeoutId) {
      clearTimeout(this.autoLoadCheckTimeoutId);
      this.autoLoadCheckTimeoutId = undefined;
    }
    if (this.refreshCooldownTimeoutId) {
      clearTimeout(this.refreshCooldownTimeoutId);
      this.refreshCooldownTimeoutId = undefined;
    }
    // Reset loading state when component is destroyed
    this.isLoading = false;
    this.loadingPhase = null;
  }

  private scheduleAutoLoadCheck(): void {
    if (this.autoLoadCheckTimeoutId) {
      return;
    }
    this.autoLoadCheckTimeoutId = window.setTimeout(() => {
      this.autoLoadCheckTimeoutId = undefined;
      this.checkViewActiveAndMaybeAutoLoad();
    }, 0);
  }

  private checkViewActiveAndMaybeAutoLoad(): void {
    const activeNow = this.computeIsViewActive();
    if (!activeNow) {
      if (this.isViewActive) {
        this.onBecameInactive();
      }
      return;
    }

    if (!this.isViewActive) {
      this.onBecameActive();
    }

    this.maybeScheduleAutoLoad();
  }

  private onBecameActive(): void {
    this.isViewActive = true;
    this.hasTriggeredLoadWhileActive = false;
  }

  private onBecameInactive(): void {
    this.isViewActive = false;
    this.hasTriggeredLoadWhileActive = false;
    this.cancelPendingAutoLoad();
    this.cancelActiveLoad();
  }

  private maybeScheduleAutoLoad(): void {
    if (!this.isViewActive) {
      return;
    }
    if (this.hasLoadedSuccessfullyOnce) {
      this.cancelPendingAutoLoad();
      return;
    }
    if (this.isLoadInFlight) {
      return;
    }
    if (this.hasTriggeredLoadWhileActive || this.autoLoadTimeoutId) {
      return;
    }

    const stallMs = Math.max(0, Math.round(this.autoLoadStallMs || 0));
    if (stallMs > 0) {
      this.isAutoLoadStalling = true;
    }
    this.autoLoadTimeoutId = window.setTimeout(() => {
      this.autoLoadTimeoutId = undefined;
      this.isAutoLoadStalling = false;

      if (!this.isViewActive) {
        return;
      }
      if (
        this.hasLoadedSuccessfullyOnce ||
        this.isLoadInFlight ||
        this.hasTriggeredLoadWhileActive
      ) {
        return;
      }

      this.hasTriggeredLoadWhileActive = true;
      this.startLoad('auto');
    }, stallMs);
  }

  private cancelPendingAutoLoad(): void {
    if (this.autoLoadTimeoutId) {
      clearTimeout(this.autoLoadTimeoutId);
      this.autoLoadTimeoutId = undefined;
    }
    this.isAutoLoadStalling = false;
  }

  private computeIsViewActive(): boolean {
    const host = this.elementRef.nativeElement;
    if (!host.isConnected) {
      return false;
    }

    const pageEl = host.querySelector('.ion-page') as HTMLElement | null;
    const el = pageEl ?? host;

    // `hidden` maps to `display: none`, so treat as inactive.
    if ((el as any).hidden) {
      return false;
    }

    // If Ionic/Angular detaches or hides the page, this will be empty.
    return el.getClientRects().length > 0;
  }

  private updateToolbarColor(): void {
    // If toolbarColor is explicitly set via input, use it
    if (this.toolbarColor) {
      this.computedToolbarColor = this.toolbarColor;
      return;
    }

    // Otherwise, use theme-based color
    const currentTheme = this.themeService.getCurrentTheme();
    if (currentTheme) {
      const theme = getTheme(currentTheme);
      if (theme) {
        // Use 'dark' color for dark themes, 'light' for light themes
        // This ensures proper contrast
        this.computedToolbarColor = theme.isDark ? 'dark' : 'light';
      }
    }
  }

  get shouldShowFooter(): boolean {
    if (this.showFooter !== null) {
      return this.showFooter;
    }
    return this.footerText !== null && this.footerText !== '';
  }

  /** Classes merged onto `ion-content` (padding utilities + optional fill mode). */
  get contentHostClasses(): string {
    const raw = (this.contentClass ?? '').trim();
    const tokens = raw ? raw.split(/\s+/) : [];
    const withoutBottomPadding = this.contentSlotFill
      ? tokens.filter((c) => c !== 'ion-padding-bottom' && c !== 'ion-padding')
      : tokens;
    const parts = [...withoutBottomPadding];
    if (this.contentSlotFill) {
      parts.push('crowdctrl-page-ion-content--fill');
    }
    return parts.join(' ');
  }

  /**
   * Computed progress bar color based on loading phase.
   * Light gray (medium) during stall, green (success) during active loading.
   */
  get computedProgressBarColor(): string {
    if (this.isAutoLoadStalling) {
      return 'medium';
    }
    if (this.loadingPhase === 'stall') {
      return 'medium';
    } else if (this.loadingPhase === 'active') {
      return 'success';
    }
    return this.progressBarColor;
  }

  private cancelActiveLoad(): void {
    if (this.activeLoadSubscription) {
      this.activeLoadSubscription.unsubscribe();
    }
  }

  private startRefreshCooldown(): void {
    const cooldownMs = Math.max(0, Math.round(this.refreshCooldownMs || 0));
    if (cooldownMs <= 0) {
      return;
    }
    this.isRefreshCoolingDown = true;
    if (this.refreshCooldownTimeoutId) {
      clearTimeout(this.refreshCooldownTimeoutId);
    }
    this.refreshCooldownTimeoutId = window.setTimeout(() => {
      this.isRefreshCoolingDown = false;
      this.refreshCooldownTimeoutId = undefined;
    }, cooldownMs);
  }

  private startLoad(kind: 'auto' | 'refresh', completeRefresher?: () => void): void {
    if (this.activeLoadSubscription) {
      completeRefresher?.();
      return;
    }

    const fn = kind === 'refresh' ? (this.refreshFn ?? this.loadFn) : this.loadFn;

    // Backwards-compatible events (for callers that still use `(load)` / `(refresh)`).
    if (!fn) {
      if (kind === 'refresh') {
        this.refresh.emit();
      } else {
        this.load.emit();
      }
      completeRefresher?.();
      return;
    }

    this.isLoadInFlight = true;
    let didComplete = false;
    let didError = false;

    try {
      this.activeLoadSubscription = fn()
        .pipe(
          finalize(() => {
            const didEnd = didComplete || didError;
            this.activeLoadSubscription = undefined;
            this.isLoadInFlight = false;

            completeRefresher?.();

            if (kind === 'refresh' && didEnd) {
              this.startRefreshCooldown();
            }

            if (didComplete && !didError) {
              this.hasLoadedSuccessfullyOnce = true;
            }
          }),
        )
        .subscribe({
          complete: () => {
            didComplete = true;
          },
          error: () => {
            didError = true;
          },
        });
    } catch (error) {
      console.error('[PageComponent] loadFn threw:', error);
      this.activeLoadSubscription = undefined;
      this.isLoadInFlight = false;
      completeRefresher?.();
      if (kind === 'refresh') {
        this.startRefreshCooldown();
      }
    }
  }

  onRefresh(force = false): void {
    if (this.toolbarBusy) {
      return;
    }
    // Do not gate on `LoadingService` global `isLoading`: other HTTP (e.g. TTS, child
    // components) would block toolbar refresh while this page's own load is idle.
    if (!force && (this.isLoadInFlight || this.isRefreshCoolingDown)) {
      return;
    }
    this.cancelPendingAutoLoad();
    this.startLoad('refresh');
  }

  onIonRefresh(event: any): void {
    const target = event.target as HTMLIonRefresherElement;
    const complete = () => target.complete();

    if (this.toolbarBusy) {
      complete();
      return;
    }

    if (this.showPwaPullRefresher) {
      void this.pwaUpdateService.checkForUpdates().finally(() => {
        if (this.showRefresh) {
          if (this.isLoadInFlight || this.isRefreshCoolingDown) {
            complete();
            return;
          }
          this.cancelPendingAutoLoad();
          this.startLoad('refresh', complete);
        } else {
          complete();
        }
      });
      return;
    }

    if (this.isLoadInFlight || this.isRefreshCoolingDown) {
      complete();
      return;
    }
    this.cancelPendingAutoLoad();
    this.startLoad('refresh', complete);
  }

  async onCheckForUpdates(): Promise<void> {
    // Show a toast indicating we're checking for updates
    const checkingToast = await this.toastService.create({
      message: 'Checking for updates...',
      duration: 2000,
      showDismissButton: false,
    });
    await checkingToast.present();

    // Check for updates
    try {
      await this.pwaUpdateService.checkForUpdates();

      // Dismiss the checking toast
      await checkingToast.dismiss();

      // Show a toast indicating check is complete
      await this.toastService.show({
        message: 'Update check complete. You will be notified if an update is available.',
        duration: 3000,
      });
    } catch (error) {
      // Dismiss the checking toast
      await checkingToast.dismiss();

      // Show error toast
      await this.toastService.show({
        message: 'Error checking for updates. Please try again later.',
        duration: 3000,
        color: 'danger',
      });
    }
  }
}
